/**
 * Validation utilities for forms
 */

export interface ValidationError {
  field: string;
  message: string;
}

export interface PasswordRequirements {
  hasMinLength: boolean;
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasNumber: boolean;
  hasSpecialChar: boolean;
}

// Email validation
export const validateEmail = (email: string): string | null => {
  if (!email.trim()) {
    return 'Email is required';
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return 'Please enter a valid email address';
  }

  return null;
};

// Password validation
export const validatePassword = (password: string): string | null => {
  if (!password) {
    return 'Password is required';
  }

  if (password.length < 8) {
    return 'Password must be at least 8 characters';
  }

  return null;
};

// Check password requirements
export const checkPasswordRequirements = (
  password: string
): PasswordRequirements => {
  return {
    hasMinLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /\d/.test(password),
    hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
  };
};

// Strong password check (all requirements)
export const isStrongPassword = (password: string): boolean => {
  const requirements = checkPasswordRequirements(password);
  return (
    requirements.hasMinLength &&
    requirements.hasUppercase &&
    requirements.hasLowercase &&
    requirements.hasNumber &&
    requirements.hasSpecialChar
  );
};

// Validate password confirmation
export const validatePasswordConfirmation = (
  password: string,
  confirmation: string
): string | null => {
  if (!confirmation) {
    return 'Please confirm your password';
  }

  if (password !== confirmation) {
    return 'Passwords do not match';
  }

  return null;
};

// Validate company name
export const validateCompanyName = (name: string): string | null => {
  if (!name.trim()) {
    return 'Company name is required';
  }

  if (name.length < 2) {
    return 'Company name must be at least 2 characters';
  }

  if (name.length > 100) {
    return 'Company name must be less than 100 characters';
  }

  return null;
};

// Validate phone number (basic)
export const validatePhone = (phone: string): string | null => {
  if (!phone.trim()) {
    return 'Phone number is required';
  }

  // Allow various formats: (555) 123-4567, 555-123-4567, 5551234567, +1-555-123-4567
  const phoneRegex = /^[\d\s\-()+]*\d[\d\s\-()]*\d[\d\s\-()]*\d$/.test(phone);

  if (!phoneRegex) {
    return 'Please enter a valid phone number';
  }

  return null;
};

// Check if email is already taken (simulated - would call API)
export const checkEmailAvailability = async (
  email: string
): Promise<boolean> => {
  // In real app, call: POST /auth/check-email with {email}
  // Returns {available: boolean}
  // Simulated: always available for now
  return true;
};

// Form validation helper
export const getFieldError = (
  errors: Record<string, string>,
  field: string
): string | null => {
  return errors[field] || null;
};

// Check if form has errors
export const hasFormErrors = (errors: Record<string, string>): boolean => {
  return Object.values(errors).some((error) => error !== '');
};

// Sanitize email
export const sanitizeEmail = (email: string): string => {
  return email.toLowerCase().trim();
};

// Format display email
export const formatEmailForDisplay = (email: string): string => {
  return email.toLowerCase().trim();
};
